import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Paper,
  Avatar,
  CircularProgress
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  ShoppingCart as OrderIcon,
  Business as SupplierIcon,
  SwapHoriz as TransactionIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingIcon,
  Upload as UploadIcon,
  Add as AddIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import api from '../axiosConfig';
import { useAuth } from '../AuthContext';
import { getHomeRouteForRole } from '../utils/homeRoute';

const StockDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProducts: 0,
    lowStock: 0,
    pendingOrders: 0,
    suppliers: 0
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      // Fetch product stats
      const productsResponse = await api.get('/products');
      const productsData = productsResponse.data?.data || productsResponse.data || [];
      const products = Array.isArray(productsData) ? productsData : [];
      
      // Fetch supplier stats
      const suppliersResponse = await api.get('/suppliers');
      const suppliersData = suppliersResponse.data?.data || suppliersResponse.data || [];
      const suppliers = Array.isArray(suppliersData) ? suppliersData : [];
      
      // Fetch purchase order stats
      let pendingOrders = 0;
      try {
        const ordersResponse = await api.get('/purchase-orders/');
        const ordersData = ordersResponse.data?.data || ordersResponse.data || [];
        const orders = Array.isArray(ordersData) ? ordersData : [];
        
        // Count pending orders (not received or cancelled)
        pendingOrders = orders.filter(order => 
          order.status && !['received', 'cancelled'].includes(order.status.toLowerCase())
        ).length;
      } catch (orderError) {
        console.warn('Could not fetch purchase orders:', orderError);
      }
      
      // Calculate low stock items
      const lowStockItems = products.filter(p => 
        p.reorder_level && p.current_stock <= p.reorder_level
      );

      setStats({
        totalProducts: products.length,
        lowStock: lowStockItems.length,
        pendingOrders: pendingOrders,
        suppliers: suppliers.length
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const quickStats = [
    {
      title: 'Total Products',
      value: stats.totalProducts,
      icon: <InventoryIcon />,
      color: '#2196F3',
      subtitle: 'Active in system',
      action: () => navigate('/stock/products')
    },
    {
      title: 'Low Stock Items',
      value: stats.lowStock,
      icon: <WarningIcon />,
      color: stats.lowStock > 0 ? '#FF9800' : '#4CAF50',
      subtitle: stats.lowStock > 0 ? 'Need reordering' : 'All good',
      action: () => navigate('/stock/products?filter=low')
    },
    {
      title: 'Pending Orders',
      value: stats.pendingOrders,
      icon: <OrderIcon />,
      color: stats.pendingOrders > 0 ? '#2196F3' : '#4CAF50',
      subtitle: stats.pendingOrders > 0 ? 'Awaiting delivery' : 'All received',
      action: () => navigate('/stock/purchase-orders')
    },
    {
      title: 'Active Suppliers',
      value: stats.suppliers,
      icon: <SupplierIcon />,
      color: '#9C27B0',
      subtitle: 'In your network',
      action: () => navigate('/supplier_card')
    }
  ];

  const actions = [
    {
      id: 'products',
      title: 'Product Management',
      description: 'View, add, and manage all your salon products and inventory.',
      icon: <InventoryIcon sx={{ fontSize: 28 }} />,
      iconBg: 'linear-gradient(135deg, #E91E63, #C2185B)',
      path: '/stock/products',
      status: `${stats.totalProducts} products`,
      quickActions: [
        { label: 'View All Products', path: '/stock/products' },
        { label: 'Add New Product', path: '/stock/products' },
        { label: 'Import Products', path: '/calculator/import?tab=products' }
      ]
    },
    {
      id: 'transactions',
      title: 'Stock Transactions',
      description: 'Track stock movements, deliveries, and usage history.',
      icon: <TransactionIcon sx={{ fontSize: 28 }} />,
      iconBg: 'linear-gradient(135deg, #2196F3, #1976D2)',
      path: '/stock/transactions',
      status: 'Track movements',
      quickActions: [
        { label: 'View Transactions', path: '/stock/transactions' },
        { label: 'Record Delivery', path: '/stock/transactions' },
        { label: 'Stock Take', path: '/stock/transactions' }
      ]
    },
    {
      id: 'orders',
      title: 'Purchase Orders',
      description: 'Create and manage orders to suppliers.',
      icon: <OrderIcon sx={{ fontSize: 28 }} />,
      iconBg: 'linear-gradient(135deg, #4CAF50, #45a049)',
      path: '/stock/purchase-orders',
      status: `${stats.pendingOrders} pending`,
      quickActions: [
      { label: 'View Orders', path: '/stock/purchase-orders' },
        { label: 'Create Order', path: '/stock/purchase-orders' },
        { label: 'Receive Stock', path: '/stock/purchase-orders' }
      ]
    },
    {
      id: 'suppliers',
      title: 'Supplier Management',
      description: 'Manage your supplier contacts and relationships.',
      icon: <SupplierIcon sx={{ fontSize: 28 }} />,
      iconBg: 'linear-gradient(135deg, #FF9800, #F57C00)',
      path: '/supplier_directory',
      status: `${stats.suppliers} suppliers`,
      quickActions: [
      { label: 'View Suppliers', path: '/supplier_directory' },
        { label: 'Add Supplier', path: '/supplier_card?mode=new' },
        { label: 'Import Suppliers', path: '/import/suppliers' }
      ]
    },
  ];
    
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #E91E63, #C2185B)',
          color: 'white',
          p: 4,
          mb: 4,
          borderRadius: 2
        }}
      >
        <Container maxWidth="lg">
          <Button
            startIcon={<HomeIcon />}
            onClick={() => navigate(getHomeRouteForRole(user?.role))}
            sx={{ 
              color: 'white', 
              mb: 2,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            Back to Hub
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Stock Management
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Manage products, inventory, suppliers, and purchase orders
          </Typography>
        </Container>
      </Paper>

      <Container maxWidth="lg">
        {/* Quick Stats */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {quickStats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card 
                elevation={0} 
                sx={{ 
                  border: '1px solid #e9ecef',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.1)'
                  }
                }}
                onClick={stat.action}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Avatar sx={{ bgcolor: `${stat.color}20`, color: stat.color }}>
                      {stat.icon}
                    </Avatar>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: stat.color, mb: 0.5 }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {stat.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stat.subtitle}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Main Action Cards */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: '#2c3e50' }}>
          What would you like to do?
        </Typography>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {actions.map((action) => (
            <Grid item xs={12} md={6} key={action.id}>
              <Card
                elevation={0}
                sx={{
                  border: '1px solid #e9ecef',
                  height: '100%',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  {/* Icon & Status */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        background: action.iconBg
                      }}
                    >
                      {action.icon}
                    </Avatar>
                    <Chip
                      label={action.status}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>

                  {/* Title & Description */}
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {action.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {action.description}
                  </Typography>

                  {/* Quick Actions */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                      QUICK ACTIONS:
                    </Typography>
                    {action.quickActions.map((qa, idx) => (
                      <Button
                        key={idx}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(qa.path);
                        }}
                        sx={{
                          display: 'block',
                          textAlign: 'left',
                          textTransform: 'none',
                          mb: 0.5,
                          justifyContent: 'flex-start',
                          color: '#667eea',
                          '&:hover': {
                            bgcolor: '#667eea10'
                          }
                        }}
                      >
                        → {qa.label}
                      </Button>
                    ))}
                  </Box>

                  {/* Main Action Button */}
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => navigate(action.path)}
                    sx={{
                      background: action.iconBg,
                      textTransform: 'none',
                      py: 1.5,
                      fontWeight: 600,
                      '&:hover': {
                        opacity: 0.9
                      }
                    }}
                  >
                    Open {action.title}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Quick Start Tips */}
        {stats.totalProducts === 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: '2px dashed #e9ecef',
              bgcolor: '#f8f9fa',
              textAlign: 'center'
            }}
          >
            <InventoryIcon sx={{ fontSize: 48, color: '#9e9e9e', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              Get Started with Stock Management
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              You haven't added any products yet. Start by importing your product list or adding products manually.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={() => navigate('/calculator/import?tab=products')}
                sx={{
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  textTransform: 'none'
                }}
              >
                Import Products
              </Button>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => navigate('/stock/products')}
                sx={{ textTransform: 'none' }}
              >
                Add Manually
              </Button>
            </Box>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default StockDashboard;